import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/common/Icon';
import Button from 'components/common/Button';
import ConfirmDialog from 'components/common/ConfirmDialog';
import { useClickOutside } from 'hooks/useClickOutside';
import { usePermissions } from 'hooks/usePermissions';
import { MODULES, ACTIONS } from 'constants/permissions';
import { newspaperService } from 'services/newspaperService';
import './ManualUploadNotification.css';

const ManualUploadNotification = () => {
  const { can } = usePermissions();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  const [failedJobs, setFailedJobs] = useState([]);
  const [missedJobs, setMissedJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [targetItemToDismiss, setTargetItemToDismiss] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const hasPermission = can(MODULES.STAGE1, ACTIONS.MANUAL_UPLOAD);

  const fetchDailySummary = useCallback(async () => {
    if (!hasPermission) return;
    try {
      setLoading(true);
      const data = await newspaperService.getDailySummary();
      setFailedJobs(data.failed || []);
      setMissedJobs(data.missed || []);
    } catch (err) {
      console.error('Failed to fetch daily summary for manual upload notification:', err);
    } finally {
      setLoading(false);
    }
  }, [hasPermission]);

  useEffect(() => {
    if (hasPermission) {
      fetchDailySummary();

      const onFocus = () => fetchDailySummary();
      window.addEventListener('focus', onFocus);

      // Listen for custom event triggered after successful manual upload
      const onManualUploadSuccess = () => fetchDailySummary();
      window.addEventListener('kvo:manual-upload-success', onManualUploadSuccess);

      return () => {
        window.removeEventListener('focus', onFocus);
        window.removeEventListener('kvo:manual-upload-success', onManualUploadSuccess);
      };
    }
  }, [hasPermission, fetchDailySummary]);

  useClickOutside(wrapperRef, () => setIsOpen(false), isOpen);

  const handleBellClick = () => {
    if (!isOpen) {
      fetchDailySummary();
    }
    setIsOpen((v) => !v);
  };

  const handleStartManualUpload = (item) => {
    setIsOpen(false);
    navigate(`/stage1?manualUpload=true&newspaperName=${encodeURIComponent(item.newspaper_name)}&edition=${encodeURIComponent(item.edition)}`);
  };

  const handleCancelClick = (item) => {
    setTargetItemToDismiss(item);
    setIsConfirmOpen(true);
  };

  const handleConfirmDismiss = async () => {
    if (!targetItemToDismiss) return;
    try {
      await newspaperService.dismissNotification(targetItemToDismiss.id);
      await fetchDailySummary();
      if (failedJobs.length + missedJobs.length <= 1) {
          setIsOpen(false);
      }
    } catch (err) {
      console.error('Failed to dismiss notification:', err);
    } finally {
      setIsConfirmOpen(false);
      setTargetItemToDismiss(null);
    }
  };

  if (!hasPermission) return null;

  const totalCount = failedJobs.length + missedJobs.length;
  const allItems = [
    ...failedJobs.map((j) => ({ ...j, type: 'Failed' })),
    ...missedJobs.map((j) => ({ ...j, type: 'Missed' })),
  ];

  return (
    <div className="kvo-manual-notif-bell" ref={wrapperRef}>
      <button
        type="button"
        className="kvo-header__icon-btn"
        onClick={handleBellClick}
        aria-label={totalCount > 0 ? `${totalCount} newspapers require manual upload` : 'Manual Upload Notifications'}
      >
        <Icon name="upload" size={20} />
        {totalCount > 0 && (
          <span className="kvo-manual-notif-bell__badge" aria-hidden="true">
            {totalCount > 9 ? '9+' : totalCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="kvo-manual-notif-panel" role="dialog" aria-label="Manual Upload Notifications">
          <div className="kvo-manual-notif-panel__header">
            <div>
              <div className="kvo-manual-notif-panel__title">Manual Upload Required</div>
              <div className="kvo-manual-notif-panel__subtitle">The following newspapers were not processed automatically today.</div>
            </div>
          </div>

          <div className="kvo-manual-notif-panel__body">
            {loading && allItems.length === 0 ? (
              <div className="kvo-manual-notif-panel__empty">
                <span>Loading…</span>
              </div>
            ) : allItems.length === 0 ? (
              <div className="kvo-manual-notif-panel__empty">
                <Icon name="check" size={20} />
                <span>All automated processes succeeded today.</span>
              </div>
            ) : (
              allItems.map((item) => (
                <div key={item.id} className="kvo-manual-notif-item">
                  <div className="kvo-manual-notif-item__row">
                    <div className="kvo-manual-notif-item__details">
                      <div className="kvo-manual-notif-item__title">{item.newspaper_name}</div>
                      <div className="kvo-manual-notif-item__meta">Edition : {item.edition}</div>
                      <div className="kvo-manual-notif-item__meta">Source : {item.source}</div>
                      <div className="kvo-manual-notif-item__meta">
                        Status : <span className={`kvo-manual-notif-item__status kvo-manual-notif-item__status--${item.type.toLowerCase()}`}>{item.type}</span>
                      </div>
                    </div>
                    <div className="kvo-manual-notif-item__action" style={{ display: 'flex', gap: '8px' }}>
                      <Button size="sm" variant="outline" onClick={() => handleCancelClick(item)}>
                        Cancel
                      </Button>
                      <Button size="sm" onClick={() => handleStartManualUpload(item)}>
                        Start Manual Upload
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={isConfirmOpen}
        title="Remove Notification"
        message={`Are you sure you want to remove this notification for ${targetItemToDismiss?.newspaper_name}?`}
        confirmLabel="Yes"
        cancelLabel="No"
        confirmVariant="danger"
        onConfirm={handleConfirmDismiss}
        onCancel={() => {
          setIsConfirmOpen(false);
          setTargetItemToDismiss(null);
        }}
      />
    </div>
  );
};

export default ManualUploadNotification;
